import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import { FrontendSettings, FrontendSettingsResponse } from '@/types/userSettings';
import ajax from '@/utils/ajax';
import { getToken } from '@/utils/getToken';

const PATH = '/api/users/settings/';

export const userSettingsRepository = {
  async getFrontendSettings({ host }: { host: string }): Promise<FrontendSettings> {
    const { data } = await ajax.get({ url: `${host}${PATH}` });

    return (data as FrontendSettingsResponse).frontend_settings ?? {};
  },

  /**
   * Sends only the changed keys. The backend shallow-merges them, and a null
   * value deletes its key.
   */
  async patchFrontendSettings({
    host,
    settings
  }: {
    host: string;
    settings: FrontendSettings;
  }): Promise<FrontendSettings> {
    const { data } = await ajax.patch<{ frontend_settings: FrontendSettings }>({
      url: `${host}${PATH}`,
      data: { frontend_settings: settings }
    });

    return (data as unknown as FrontendSettingsResponse).frontend_settings ?? {};
  },

  /**
   * The last push before the tab goes away.
   *
   * Browsers cancel ordinary XHR and fetch work once unload begins, so this one
   * is sent `keepalive` and its result is never awaited. `sendBeacon` would be
   * the usual tool but cannot carry the `Authorization` header this API needs.
   */
  flushFrontendSettings({ host, settings }: { host: string; settings: FrontendSettings }): void {
    const token = getToken(host) as string | undefined;

    try {
      void fetch(`${host}${PATH}`, {
        method: 'PATCH',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ frontend_settings: settings })
      }).catch(() => {
        // The tab is going away; there is nothing left to react to.
      });
    } catch {
      // Same: a rejected send is the change not surviving, which is the
      // pre-existing behaviour, not something to surface.
    }
  }
};

const UserSettingsRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async getFrontendSettings(): Promise<FrontendSettings> {
      return userSettingsRepository.getFrontendSettings({ host });
    },
    async patchFrontendSettings(settings: FrontendSettings): Promise<FrontendSettings> {
      return userSettingsRepository.patchFrontendSettings({ host, settings });
    },
    flushFrontendSettings(settings: FrontendSettings): void {
      userSettingsRepository.flushFrontendSettings({ host, settings });
    }
  };
};

export default UserSettingsRepository;
